import React from 'react'
import { Play, Pause, SkipBack, SkipForward, Download, ChevronsUpDown } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Tooltip } from '../../../../components/ui/tooltip'
import Popover from '../../../../components/ui/popover'
import { track } from '../../../../lib/analytics/posthog'
import { getPodcastTimestamp, savePodcastTimestamp, clearPodcastTimestamp } from '../../../../lib/podcast-timestamps'
import { markPodcastPlayedForCurrentUser } from '../../../../api/podcasts'

interface EditorAudioPlayerProps {
  audioUrl: string
  durationSeconds?: number | null
  className?: string
  fileName?: string // retained for potential future naming but currently unused
  onDownload?: () => void // triggers smart export / download logic from parent
  podcastId?: string // ID of the podcast to persist playback position
  onEnded?: () => void // optional: notify parent when track finishes (for queues)
  autoPlay?: boolean // optional: whether to start playing immediately when mounted/changed
  onTimeUpdate?: (currentTime: number, duration: number) => void // emits progress for external indicators
  onPlayStateChange?: (isPlaying: boolean) => void // emits when play/pause state changes
  compact?: boolean // when true, renders ONLY the play/pause button (for embedding in tighter sticky players)
}

export interface EditorAudioPlayerHandle {
  togglePlay: () => void
  play: () => void
  pause: () => void
}

// Accessible, custom-styled audio player optimized for the narrow editor footer.
// Features: play/pause, scrub, skip ±15s, playback rate menu, download, keyboard controls.
export const EditorAudioPlayer = React.forwardRef<EditorAudioPlayerHandle, EditorAudioPlayerProps>(({
  audioUrl,
  durationSeconds,
  className,
  // fileName intentionally unused now; download handled upstream
  onDownload,
  podcastId,
  onEnded,
  autoPlay,
  onTimeUpdate,
  onPlayStateChange,
  compact,
}, ref) => {
  const audioRef = React.useRef<HTMLAudioElement | null>(null)
  const progressBarRef = React.useRef<HTMLDivElement | null>(null)
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [currentTime, setCurrentTime] = React.useState(0)
  const [duration, setDuration] = React.useState(durationSeconds ?? 0)
  const [playbackRate, setPlaybackRate] = React.useState(1)
  const [rateMenuOpen, setRateMenuOpen] = React.useState(false)
  const hasSentStartRef = React.useRef(false)
  const hasSentFinishRef = React.useRef(false)
  const onTimeUpdateRef = React.useRef(onTimeUpdate)
  React.useEffect(() => { onTimeUpdateRef.current = onTimeUpdate }, [onTimeUpdate])
  const onPlayStateChangeRef = React.useRef(onPlayStateChange)
  React.useEffect(() => { onPlayStateChangeRef.current = onPlayStateChange }, [onPlayStateChange])

  // Sync duration state with durationSeconds prop when it changes
  // This is critical for when podcast generation completes and the prop updates
  React.useEffect(() => {
    if (typeof durationSeconds === 'number' && durationSeconds > 0) {
      setDuration(durationSeconds)
    }
  }, [durationSeconds])

  // Load metadata if not provided
  React.useEffect(() => {
    const el = audioRef.current
    if (!el) return
    const handleLoaded = () => {
      // Always prefer actual audio duration from metadata when available
      const actualDuration = el.duration
      if (actualDuration && isFinite(actualDuration) && actualDuration > 0) {
        setDuration(actualDuration)
      } else if (typeof durationSeconds === 'number' && durationSeconds > 0) {
        // Fall back to prop if audio metadata isn't ready
        setDuration(durationSeconds)
      }
      // Restore saved timestamp when audio is loaded
      if (podcastId && el.duration > 0) {
        const savedTime = getPodcastTimestamp(podcastId)
        if (savedTime !== null && savedTime < el.duration) {
          el.currentTime = savedTime
          setCurrentTime(savedTime)
        }
      }
    }
    const handleTime = () => {
      setCurrentTime(el.currentTime)
      if (onTimeUpdateRef.current) {
        const total = duration || el.duration || 0
        onTimeUpdateRef.current(el.currentTime, total)
      }
      // Track finish once when 90% or more of the audio has been played
      if (!hasSentFinishRef.current) {
        const total = duration || el.duration || 0
        if (total > 0 && el.currentTime / total >= 0.9) {
          hasSentFinishRef.current = true
          track('podcast_play_finished')
          // Clear timestamp when user finishes listening
          if (podcastId) {
            clearPodcastTimestamp(podcastId)
          }
        }
      }
    }
    const handlePlay = () => {
      setIsPlaying(true)
      onPlayStateChangeRef.current?.(true)
      if (!hasSentStartRef.current) {
        hasSentStartRef.current = true
        track('podcast_play_started')
        // Mark podcast as played when user first starts playing
        if (podcastId) {
          void markPodcastPlayedForCurrentUser(podcastId).catch(() => { /* noop */ })
        }
      }
    }
    const handlePause = () => {
      setIsPlaying(false)
      onPlayStateChangeRef.current?.(false)
      // Save timestamp when user pauses
      if (podcastId && el.currentTime > 0) {
        const total = duration || el.duration || 0
        savePodcastTimestamp(podcastId, el.currentTime, total)
      }
    }
    const handleEnded = () => {
      setIsPlaying(false)
      onPlayStateChangeRef.current?.(false)
      // Clear timestamp when playback ends
      if (podcastId) {
        clearPodcastTimestamp(podcastId)
      }
      // Notify parent for queue advancement
      if (onEnded) {
        try { onEnded() } catch { /* noop */ }
      }
    }
    const handleDurationChange = () => {
      // Handle case where duration becomes available after initial load
      const actualDuration = el.duration
      if (actualDuration && isFinite(actualDuration) && actualDuration > 0) {
        setDuration(actualDuration)
      }
    }
    el.addEventListener('loadedmetadata', handleLoaded)
    el.addEventListener('durationchange', handleDurationChange)
    el.addEventListener('timeupdate', handleTime)
    el.addEventListener('play', handlePlay)
    el.addEventListener('pause', handlePause)
    el.addEventListener('ended', handleEnded)
    
    // If metadata is already available (e.g., cached audio), sync duration immediately
    if (el.duration && isFinite(el.duration) && el.duration > 0) {
      setDuration(el.duration)
    }
    
    return () => {
      el.removeEventListener('loadedmetadata', handleLoaded)
      el.removeEventListener('durationchange', handleDurationChange)
      el.removeEventListener('timeupdate', handleTime)
      el.removeEventListener('play', handlePlay)
      el.removeEventListener('pause', handlePause)
      el.removeEventListener('ended', handleEnded)
    }
  }, [audioUrl, durationSeconds, podcastId, duration])

  // Reset play/finish flags when the audio source changes
  React.useEffect(() => {
    hasSentStartRef.current = false
    hasSentFinishRef.current = false
    
    if (autoPlay && audioRef.current) {
      const playPromise = audioRef.current.play()
      if (playPromise !== undefined) {
        playPromise.catch(e => {
          console.warn("Auto-play prevented:", e)
        })
      }
    }
  }, [audioUrl, autoPlay])

  // Periodically save timestamp during playback (every 5 seconds)
  React.useEffect(() => {
    if (!podcastId || !isPlaying) return
    
    const intervalId = setInterval(() => {
      const el = audioRef.current
      if (el && el.currentTime > 0) {
        const total = duration || el.duration || 0
        savePodcastTimestamp(podcastId, el.currentTime, total)
      }
    }, 5000) // Save every 5 seconds

    return () => clearInterval(intervalId)
  }, [isPlaying, podcastId, duration])

  // Save timestamp when component unmounts
  React.useEffect(() => {
    const el = audioRef.current
    return () => {
      if (podcastId && el && el.currentTime > 0) {
        const total = duration || el.duration || 0
        savePodcastTimestamp(podcastId, el.currentTime, total)
      }
    }
  }, [podcastId, duration])

  // Popover handles outside click/Escape; no manual listener needed

  const togglePlay = async () => {
    const el = audioRef.current
    if (!el) return
    try {
      if (isPlaying) {
        el.pause()
      } else {
        await el.play()
      }
    } catch {
      // no-op; autoplay may be blocked
    }
  }

  React.useImperativeHandle(ref, () => ({
    togglePlay: () => { void togglePlay() },
    play: () => { void audioRef.current?.play().catch(() => { /* noop */ }) },
    pause: () => { audioRef.current?.pause() },
  }), [isPlaying])

  const seekRelative = (delta: number) => {
    const el = audioRef.current
    if (!el) return
    el.currentTime = Math.min(Math.max(0, el.currentTime + delta), duration || el.duration || 0)
  }

  const handleScrub = (clientX: number) => {
    const bar = progressBarRef.current
    const el = audioRef.current
    if (!bar || !el) return
    const rect = bar.getBoundingClientRect()
    const ratio = Math.min(Math.max(0, (clientX - rect.left) / rect.width), 1)
    el.currentTime = ratio * (duration || el.duration || 0)
  }

  const onBarPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    handleScrub(e.clientX)
    const move = (ev: PointerEvent) => handleScrub(ev.clientX)
    const up = () => {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', up)
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', up)
  }

  // Keyboard accessibility for slider
  const onSliderKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') { seekRelative(5); e.preventDefault() }
    if (e.key === 'ArrowLeft') { seekRelative(-5); e.preventDefault() }
    if (e.key === 'Home') { const el = audioRef.current; if (el) el.currentTime = 0; e.preventDefault() }
    if (e.key === 'End') { const el = audioRef.current; if (el) el.currentTime = duration; e.preventDefault() }
  }

  const format = (s: number) => {
    if (!isFinite(s)) return '0:00'
    const m = Math.floor(s / 60)
    const r = Math.floor(s % 60)
    return `${m}:${r.toString().padStart(2, '0')}`
  }

  const progress = duration > 0 ? currentTime / duration : 0
  const buffered = React.useMemo(() => {
    const el = audioRef.current
    if (!el || el.buffered.length === 0 || !duration) return 0
    return el.buffered.end(el.buffered.length - 1) / duration
  }, [currentTime, duration])

  const rates = [0.75, 1, 1.25, 1.5, 1.75, 2]

  const changeRate = (r: number) => {
    const el = audioRef.current
    setPlaybackRate(r)
    if (el) el.playbackRate = r
    setRateMenuOpen(false)
  }

  return (
    <div className={"flex w-full items-center gap-3 sm:gap-3 justify-center sm:justify-start px-3 py-1 sm:p-0 rounded-full sm:rounded-none bg-black/5 sm:bg-transparent border border-black/5 sm:border-0 backdrop-blur-xs sm:backdrop-blur-0 " + (className || '')}>
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
      {/* Core play button (always visible) */}
      <button
        type="button"
        onClick={togglePlay}
        className={"rounded-full flex items-center justify-center bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 transition shadow-sm cursor-pointer flex-shrink-0 " + (compact ? "h-10 w-10" : "h-11 w-11 sm:h-10 sm:w-10")}
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        <span className="relative flex items-center justify-center">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={isPlaying ? 'pause' : 'play'}
              initial={{ opacity: 0, scale: 0.6, rotate: -12 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.4, rotate: 12 }}
              transition={{ duration: 0.08, ease: 'easeOut' }}
              className="flex items-center justify-center"
            >
              {isPlaying ? <Pause className={compact ? "h-5 w-5" : "h-6 w-6 sm:h-5 sm:w-5"} /> : <Play className={compact ? "h-5 w-5" : "h-6 w-6 sm:h-5 sm:w-5"} />}
            </motion.span>
          </AnimatePresence>
        </span>
      </button>
      {compact ? null : (<>
      {/* Skip buttons (hide on mobile; show from md up) */}
      <button
        type="button"
        onClick={() => seekRelative(-15)}
        className="hidden md:flex h-9 w-9 rounded-full items-center justify-center text-foreground/60 hover:text-foreground hover:bg-black/5 active:bg-black/10 transition cursor-pointer"
        title="Back 15 sec"
        aria-label="Skip back 15 seconds"
      >
        <SkipBack className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => seekRelative(30)}
        className="hidden md:flex h-9 w-9 rounded-full items-center justify-center text-foreground/60 hover:text-foreground hover:bg-black/5 active:bg-black/10 transition cursor-pointer"
        title="Forward 30 sec"
        aria-label="Skip forward 30 seconds"
      >
        <SkipForward className="h-4 w-4" />
      </button>
      {/* Playback speed + download cluster (always) */}
      <div className="flex items-center gap-1 relative flex-shrink-0">
        <Popover
          open={rateMenuOpen}
          onOpenChange={setRateMenuOpen}
          side="top"
          align="end"
          className="min-w-0 w-28 p-1"
          trigger={({ ref, onClick, 'aria-expanded': expanded }) => (
            <button
              type="button"
              ref={ref as any}
              onClick={onClick}
              className="h-9 px-2 w-[66px] rounded-md text-[12px] font-medium inline-flex items-center gap-1 text-foreground/70 hover:text-foreground hover:bg-black/5 active:bg-black/10 transition cursor-pointer"
              aria-haspopup="menu"
              aria-expanded={expanded}
              aria-label="Playback speed"
            >
              <span className="flex-1 text-left">{playbackRate}x</span>
              <ChevronsUpDown className="h-3.5 w-3.5" />
            </button>
          )}
        >
          <div role="menu">
            {rates.map((r) => (
              <button
                key={r}
                onClick={() => changeRate(r)}
                role="menuitemradio"
                aria-checked={r === playbackRate}
                className={`w-full text-left rounded-md px-2 py-1 text-[12px] font-medium cursor-pointer transition ${r === playbackRate ? 'bg-brand-50 text-brand-700' : 'text-foreground/70 hover:bg-black/5 hover:text-foreground'}`}
              >
                {r}x {r === 1 ? 'Normal' : ''}
              </button>
            ))}
          </div>
        </Popover>
        <Tooltip content="Download episode">
          <button
            type="button"
            onClick={onDownload}
            className="h-9 w-9 rounded-md flex items-center justify-center text-foreground/60 hover:text-foreground hover:bg-black/5 active:bg-black/10 transition cursor-pointer"
            aria-label="Download episode"
          >
            <Download className="h-4 w-4" />
          </button>
        </Tooltip>
      </div>
      {/* Progress + times (hidden on mobile; shrunk on small tablets) */}
      <div className="hidden sm:flex items-center gap-2 md:gap-3 flex-1 min-w-0 pl-2">
        <span className="tabular-nums text-[11px] font-medium text-foreground/60 w-[40px] text-right select-none hidden md:inline">{format(currentTime)}</span>
        <div
          ref={progressBarRef}
          role="slider"
          aria-valuemin={0}
          aria-valuemax={Math.floor(duration)}
          aria-valuenow={Math.floor(currentTime)}
          aria-valuetext={`${format(currentTime)} elapsed of ${format(duration)}`}
          aria-label="Seek"
          tabIndex={0}
          onKeyDown={onSliderKey}
          onPointerDown={onBarPointerDown}
          className="relative h-2.5 rounded-full bg-foreground/10 group cursor-pointer touch-none select-none overflow-hidden flex-1 max-w-[180px] md:max-w-none"
        >
          <div
            className="absolute inset-y-0 left-0 bg-brand-500/15"
            style={{ width: `${buffered * 100}%` }}
          />
          <div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-brand-500 to-brand-600"
            style={{ width: `${progress * 100}%` }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-white ring-1 ring-black/10 shadow transition opacity-0 group-hover:opacity-100 translate-x-[-50%] pointer-events-none"
            style={{ left: `${progress * 100}%` }}
          />
        </div>
        <span className="tabular-nums text-[11px] font-medium text-foreground/60 w-[40px] select-none hidden md:inline">{format(duration)}</span>
      </div>
      </>)}
    </div>
  )
})

EditorAudioPlayer.displayName = 'EditorAudioPlayer'

export default EditorAudioPlayer
